import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import get from 'lodash/get';
import {
  fetchAdStart,
  fetchAdSuccess,
  fetchAdFailed,
  saveApplyStart,
  saveApplySuccess,
  saveApplyFailed,
  fetchRelatedAdsStart,
  fetchRelatedAdsSuccess,
  fetchRelatedAdsFailed,
} from './actions';
import { trackingRelatedAdsModal } from '../Serp/actions';
import {
  FETCH_AD_REQUEST,
  FETCH_RELATED_ADS_REQUEST,
  SAVE_APPLY,
  NAVIGATE_AD_PAGE,
  NAVIGATE_TO_HOME,
  NAVIGATE_TO_HOME_OR_LAST_SEARCH,
} from './constants';
import clientConfig from '../../parameters/client/config';
import SourcesApi from '../sdk/sources/ApiSources';
import SearchApi from '../sdk/search/Api';
import ApiSearchError from '../sdk/search/ApiSearchError';
import {
  isSemUrlSelector,
  getSearchParameterWhatD,
  getSearchParameterWhere,
} from '../Serp/selectors';
import normalizer from '../utils/adNormalizers/normalizer';
import normalizeAd from '../normalize/ad';
import { infrastructureIdSelector } from '../Notifications/selectors';
import { isRelatedAdsFetchEnabledSelector } from './selectors';
import { trackEvent } from '../tracking/actions';
import { navigatorClickinEvent, navigatorClickinFormEvent } from '../tracking/events';
import { Router } from '../router';
import { handleSagaErrors } from '../Error/sagas';
import { countryAndVerticalSelector, getLastSearch } from '~/common/selectors/config';
import { trovitSelector } from '~/common/selectors/trovit';

const searchApi = new SearchApi(clientConfig.searchApi);

export function* getRelatedAds({ data }) {
  try {
    const isSemUrl = yield select(isSemUrlSelector);
    const trovit = yield select(trovitSelector);
    const userId = yield select(infrastructureIdSelector);
    const what = yield select(getSearchParameterWhatD);
    const where = yield select(getSearchParameterWhere);

    const searchParams = Object.assign(data, {
      what,
      where,
      trovit,
      userId,
    });

    yield put(fetchRelatedAdsStart());
    const apiResponse = yield call([searchApi, searchApi.relatedAds], {
      ...searchParams,
    });

    if (!apiResponse) {
      yield put(fetchRelatedAdsSuccess([]));
      return;
    }

    const responseData = yield call(
      [
        normalizer,
        isSemUrl ? normalizer.normalizeLandingRelatedAds : normalizer.normalizeRelatedAds,
      ],
      apiResponse,
      data.isFromRelatedModal
    );

    if (responseData.length === 0) {
      throw new ApiSearchError(404, 'No results for this query', 404);
    }

    yield put(fetchRelatedAdsSuccess(responseData));

    if (data.isFromRelatedModal) {
      yield put(trackingRelatedAdsModal(isSemUrl));
    }
  } catch (error) {
    yield put(fetchRelatedAdsFailed(error));
  }
}

export function* getAd(action) {
  try {
    yield put(fetchAdStart());

    // Complete the request data with values from config
    const { verticalName, countryCode } = yield select(countryAndVerticalSelector);
    const trovit = yield select(trovitSelector);
    const userId = yield select(infrastructureIdSelector);

    const res = yield call([searchApi, searchApi.ad], {
      id: action.id,
      verticalName,
      countryCode,
      trovit,
      userId,
    });

    const ad = normalizeAd(res, verticalName);
    yield put(fetchAdSuccess(ad));

    // Avoid fetching new related ads when user is navigating through ads in AdPage
    const isRelatedAdsFetchEnabled = yield select(isRelatedAdsFetchEnabledSelector);
    if (isRelatedAdsFetchEnabled) {
      const relatedAdsData = { data: { ad, countryCode, verticalName } };
      yield call(getRelatedAds, relatedAdsData);
    }
  } catch (error) {
    yield call(handleSagaErrors, error.code || error.status);
    yield put(fetchAdFailed(error));
  }
}

export function* navigateAdPage({ ad }) {
  if (get(ad, ['source', 'ad', 'hasAdpageForm'])) {
    yield put(trackEvent(navigatorClickinFormEvent()));
  } else {
    yield put(trackEvent(navigatorClickinEvent()));
  }

  Router.pushRoute('adpage', { id: ad.id }).then(() => window.scrollTo(0, 0));
}

export function* saveApply(action) {
  try {
    yield put(saveApplyStart());
    yield call([SourcesApi, SourcesApi.saveApply], action.data);
    yield put(saveApplySuccess({ adId: action.data.ad.id, user: action.data.userData.user }));
  } catch (error) {
    yield put(saveApplyFailed(error));
  }
}

export function* navigateToHome() {
  return yield Router.pushRoute('home').then(() => window.scrollTo(0, 0));
}

export function* navigateToHomeOrLastSearch() {
  const lastSearch = yield select(getLastSearch);

  if (lastSearch) {
    window.location.href = lastSearch;
    return false;
  }

  return Router.pushRoute('home').then(() => window.scrollTo(0, 0));
}

export default function* adPageSagas() {
  yield all([
    takeLatest(FETCH_AD_REQUEST, getAd),
    takeLatest(FETCH_RELATED_ADS_REQUEST, getRelatedAds),
    takeLatest(NAVIGATE_AD_PAGE, navigateAdPage),
    takeLatest(NAVIGATE_TO_HOME, navigateToHome),
    takeLatest(NAVIGATE_TO_HOME_OR_LAST_SEARCH, navigateToHomeOrLastSearch),
    takeLatest(SAVE_APPLY, saveApply),
  ]);
}



// WEBPACK FOOTER //
// ./app/AdPage/sagas.js